/**
 * PillarDots — 6-dot daily pillars mini-gauge (Iter. 406.fr.40 → .53)
 *
 * Extracted from MorningDebrief for reuse by:
 *   - Morning Debrief (per-day snapshot)
 *   - Dashboard header (live snapshot via /discipline/today)
 *   - InAppNotificationManager (dispatches yumix:perfect-day event)
 *
 * Emits a `yumix:pillar-state` CustomEvent whenever the local hit-count changes,
 * plus a dedicated `yumix:perfect-day` event on the miss→hit transition to 6/6.
 * The InAppNotificationManager listens to these events to fire dopamine modal
 * and urgency banner without any prop drilling.
 */
import React, { useEffect, useRef } from 'react';
import { useAuth } from '../context/AppContext';
import { cacheDisciplineSnapshot } from '../utils/offlineQueue';

export const PillarDots = ({ pillars, language = 'fr', t, source = 'unknown' }) => {
  const lastPerfectRef = useRef(false);
  const { user } = useAuth() || { user: null };

  useEffect(() => {
    if (!Array.isArray(pillars) || pillars.length === 0) return;
    const hitCount = pillars.filter((p) => p.status === 'hit').length;
    const total = pillars.length;
    const perfect = total > 0 && hitCount === total;

    // Persist snapshot so the app can render dots instantly offline.
    if (user?.user_id) {
      cacheDisciplineSnapshot(user.user_id, { pillars, hit_count: hitCount, total, cached_source: source });
    }

    // Broadcast the current state so InAppNotificationManager can react.
    try {
      window.dispatchEvent(
        new CustomEvent('yumix:pillar-state', {
          detail: {
            hitCount,
            total,
            perfect,
            source,
            pillars: pillars.map((p) => ({ key: p.key, status: p.status, pct: p.pct })),
          },
        })
      );
    } catch (_) { /* noop */ }

    // Fire perfect-day only on the transition (avoid re-firing on remount).
    if (perfect && !lastPerfectRef.current) {
      try {
        window.dispatchEvent(new CustomEvent('yumix:perfect-day', { detail: { hitCount, total, source } }));
      } catch (_) { /* noop */ }
    }
    lastPerfectRef.current = perfect;
  }, [pillars, source]);

  if (!Array.isArray(pillars) || pillars.length === 0) return null;
  const hitCount = pillars.filter((p) => p.status === 'hit').length;
  const total = pillars.length;
  const translate = t || ((fr) => fr);

  return (
    <div className="mb-3" data-testid="morning-pillars-gauge">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
          {translate('6 piliers du jour', '6 daily pillars')}
        </span>
        <span
          className="text-[11px] font-heading font-black tabular-nums text-amber-300"
          data-testid="morning-pillars-count"
        >
          {hitCount}/{total}
        </span>
      </div>
      <div className="flex items-center justify-between gap-1.5">
        {pillars.map((p) => {
          const label = language === 'fr' ? p.label_fr : p.label_en;
          const isHit = p.status === 'hit';
          const isPartial = p.status === 'partial';
          const dotColor = isHit
            ? 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]'
            : isPartial
              ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]'
              : 'bg-red-500/70 shadow-[0_0_6px_rgba(239,68,68,0.5)]';
          const ring = isHit ? 'ring-emerald-400/40' : isPartial ? 'ring-amber-400/40' : 'ring-red-500/40';
          return (
            <div
              key={p.key}
              className="flex-1 flex flex-col items-center gap-0.5"
              data-testid={`pillar-dot-${p.key}`}
              data-status={p.status}
              title={`${label} — ${p.pct}% (${p.score}/${p.max})`}
            >
              <span className="text-[13px] leading-none" aria-hidden="true">{p.emoji}</span>
              <div className={`w-2.5 h-2.5 rounded-full ring-1 ${ring} ${dotColor} transition-all`} />
              <span className="text-[8px] uppercase tracking-wider text-muted-foreground/70 leading-none">
                {label.slice(0, 4)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PillarDots;
